import type { Card, GameState } from '../game/types';
import { CUSTODIAN_FULL_PROCESSING_THRESHOLD, getCustodianTrackedTotal } from '../game/custodian';
import {
  getEnemyAiCombatHistory,
  titleOfEnemyAiCard,
  trailingSameCount,
} from './generic';
import type { EnemyAiDecision, EnemyAiDecisionContext } from './types';

const TITLES = {
  forfeiture: 'forfeiture',
  inventoryTax: 'inventory tax',
  reissuedViolence: 'reissued violence',
  salvagedWard: 'salvaged ward',
  reprocessedEssence: 'reprocessed essence',
  fullProcessing: 'full processing',
} as const;

const HARD_NO = -100000;

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function enemyMaxHp(state: GameState): number {
  return Math.max(
    1,
    Math.floor(Number((state as any).enemyMaxHpBase ?? (state.enemy as any)?.maxHp ?? state.enemy?.hp ?? 1) || 1),
  );
}

function enemyMissingHp(state: GameState): number {
  return Math.max(0, enemyMaxHp(state) - Math.max(0, Number(state.enemy?.hp ?? 0) || 0));
}

function playerEffectiveHp(state: GameState): number {
  return Math.max(0, Number(state.player?.hp ?? 0) || 0) + Math.max(0, Number(state.player?.armor ?? 0) || 0);
}

function totalUses(history: string[], title: string): number {
  const wanted = norm(title);
  return history.reduce((count, entry) => count + (norm(entry) === wanted ? 1 : 0), 0);
}

function scoreCard(card: Card, context: EnemyAiDecisionContext): { score: number; reason: string } {
  const { state } = context;
  const title = titleOfEnemyAiCard(card);
  const history = getEnemyAiCombatHistory(state);
  const sameTrail = trailingSameCount(history, title);
  const totalTracked = getCustodianTrackedTotal(state);
  const exiledAttacks = Math.max(0, Number((state as any).custodianExiledAttackCount ?? 0) || 0);
  const exiledDefenses = Math.max(0, Number((state as any).custodianExiledDefenseCount ?? 0) || 0);
  const exiledUtilities = Math.max(0, Number((state as any).custodianExiledUtilityCount ?? 0) || 0);
  const forfeitureTurns = Math.max(0, Number((state as any).custodianForfeitureTurns ?? 0) || 0);
  const missingHp = enemyMissingHp(state);
  const playerHpPressure = playerEffectiveHp(state);

  let score = 0;
  let reason = 'fallback';

  if (title === TITLES.fullProcessing) {
    if (totalTracked < CUSTODIAN_FULL_PROCESSING_THRESHOLD) {
      return { score: HARD_NO, reason: 'threshold_not_reached' };
    }
    score = 12000 + totalTracked * 250;
    reason = 'threshold_reached';
  } else if (title === TITLES.forfeiture) {
    if (forfeitureTurns > 0) {
      return { score: HARD_NO, reason: 'forfeiture_already_active' };
    }
    score = 9200;
    reason = 'start_engine';
    if (totalUses(history, TITLES.forfeiture) > 0) score -= 300;
  } else if (title === TITLES.inventoryTax) {
    if (forfeitureTurns <= 0) {
      score = 1500;
      reason = 'tax_without_forfeiture';
    } else {
      score = 8600 + Math.min(900, forfeitureTurns * 200);
      reason = 'feed_forfeiture';
    }
  } else if (title === TITLES.reissuedViolence) {
    const dmg = exiledAttacks * 9;
    if (dmg <= 0) return { score: HARD_NO, reason: 'no_exiled_attacks' };
    score = 4800 + dmg * 18;
    reason = 'attack_payoff';
    if (dmg >= playerHpPressure) score += 2600;
    if (exiledAttacks >= exiledDefenses && exiledAttacks >= exiledUtilities) score += 700;
  } else if (title === TITLES.salvagedWard) {
    const armorGain = exiledDefenses * 6;
    if (armorGain <= 0) return { score: HARD_NO, reason: 'no_exiled_defenses' };
    score = 4300 + armorGain * 14;
    reason = 'defense_payoff';
    if (Math.max(0, Number(state.enemy?.armor ?? 0) || 0) <= 12) score += 900;
    if (exiledDefenses >= exiledAttacks && exiledDefenses >= exiledUtilities) score += 600;
  } else if (title === TITLES.reprocessedEssence) {
    const heal = exiledUtilities * 3;
    if (heal <= 0) return { score: HARD_NO, reason: 'no_exiled_utilities' };
    if (missingHp <= 0) return { score: HARD_NO, reason: 'at_full_hp' };
    score = 4100 + Math.min(2400, heal * 22);
    reason = 'heal_payoff';
    if (missingHp < 8) score -= 1200;
    if (exiledUtilities >= exiledAttacks && exiledUtilities >= exiledDefenses) score += 500;
  } else {
    score = 1000;
    reason = 'unrecognized_custodian_card';
  }

  if (sameTrail >= 2 && title !== TITLES.fullProcessing) {
    score -= 1800;
    reason = `${reason}_avoid_repetition`;
  }

  return { score, reason };
}

export function pickCustodianCard(context: EnemyAiDecisionContext): EnemyAiDecision {
  const scored = context.availableCards.map((card) => {
    const { score, reason } = scoreCard(card, context);
    return { card, score, reason };
  });

  scored.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return titleOfEnemyAiCard(a.card).localeCompare(titleOfEnemyAiCard(b.card));
  });

  return {
    card: scored[0]?.card ?? null,
    reason: scored[0]?.reason ?? 'no_available_card',
    debug: scored.map((entry) => ({
      title: entry.card.title,
      score: entry.score,
      reason: entry.reason,
    })),
  };
}
